import React, { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { DateTime } from 'luxon';
import Column from '@amzn/meridian/column';
import { TableRow, TableCell } from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';
import { type CampaignConfigurationDto } from 'src/store/generated/leela-api-generated';
import { type GtiBlocklistConfigValue } from 'src/store/leela-api';
import { CreationSource } from 'src/constants';

interface TextWithSubtextProps {
  text: string;
  subtext?: string;
}

const TextWithSubtext: React.FC<TextWithSubtextProps> = ({ text, subtext }) => {
  return (
    <>
      <Text>{text}</Text>
      {subtext && (
        <Text color="secondary" type="b200">
          {subtext}
        </Text>
      )}
    </>
  );
};

export const BlocklistTableRow: React.FC<CampaignConfigurationDto> = (config) => {
  const { id, name, config: rawConfigValue, owner, updatedAt, campaignCount = 0, source } = config;
  const configValue = rawConfigValue as GtiBlocklistConfigValue | undefined;
  const navigate = useNavigate();

  const gtiCount = useMemo(() => {
    return configValue?.gtis?.length ?? 0;
  }, [configValue]);

  const [formattedDate, relativeDate] = useMemo(() => {
    let formatted = 'N/A';
    let relative: string | undefined;

    if (updatedAt && typeof updatedAt === 'string') {
      const dateTime = DateTime.fromISO(updatedAt);
      formatted = dateTime.toLocaleString(DateTime.DATETIME_FULL);
      relative = dateTime.toRelativeCalendar() ?? relative;
    }

    return [formatted, relative];
  }, [updatedAt]);

  const handleClick = () => {
    navigate(`/blocklists/${id}`);
  };

  const handleCampaignCountClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/campaigns?configId=${id}`);
  };

  const isGlobalBlocklist = source === CreationSource.ADMIN;

  const CellContent: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div style={isGlobalBlocklist ? { opacity: 0.6 } : undefined}>{children}</div>
  );

  return (
    <TableRow
      data-testid={name?.replace(/\s/g, '_')}
      highlightOnHover
      onClick={handleClick}
    >
      <TableCell alignmentVertical="top">
        <CellContent>
          <strong>{name}</strong>
        </CellContent>
      </TableCell>
      <TableCell alignmentVertical="top">
        <CellContent>{isGlobalBlocklist ? 'Admin' : (owner ?? '')}</CellContent>
      </TableCell>
      <TableCell alignmentVertical="top">
        <CellContent>{gtiCount}</CellContent>
      </TableCell>
      <TableCell alignmentVertical="top">
        <CellContent>
          {isGlobalBlocklist
            ? (
            <Text>All</Text>
              )
            : (
            <span
              style={{ cursor: 'pointer', textDecoration: 'underline', color: '#0073bb' }}
              onClick={handleCampaignCountClick}
            >
              {campaignCount}
            </span>
              )}
        </CellContent>
      </TableCell>
      <TableCell alignmentVertical="top">
        <CellContent>
          <Column spacing="200">
            <TextWithSubtext subtext={relativeDate} text={formattedDate} />
          </Column>
        </CellContent>
      </TableCell>
    </TableRow>
  );
};
